import React, { useState } from 'react';
import { FiLock, FiUser, FiMail, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { auth } from '../api';
import { PASSWORD_HINT, validatePassword } from '../utils/passwordPolicy';

function Profile({ user, setUser }) {
  const [fullName, setFullName] = useState(user.full_name);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    const trimmedName = fullName.trim();

    if (!trimmedName) {
      setProfileError('ФИО не может быть пустым');
      return;
    }

    if (trimmedName === user.full_name) {
      setProfileError('Изменений нет');
      return;
    }

    setProfileLoading(true);
    try {
      const updatedUser = await auth.updateProfile({ fullName: trimmedName });
      setUser(updatedUser);
      setFullName(updatedUser.full_name);
      setProfileSuccess('Данные профиля обновлены');
    } catch (err) {
      setProfileError(err.response?.data?.detail || 'Не удалось обновить профиль');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('Новый пароль должен отличаться от текущего');
      return;
    }

    setPasswordLoading(true);
    try {
      await auth.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Пароль успешно изменён');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Не удалось сменить пароль');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <FiUser className="h-7 w-7 text-primary-600" />
          Профиль
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Данные аккаунта и смена пароля
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Личные данные</h2>

        {profileError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <FiAlertCircle className="mr-2 flex-shrink-0" />
            {profileError}
          </div>
        )}

        {profileSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <FiCheckCircle className="mr-2 flex-shrink-0" />
            {profileSuccess}
          </div>
        )}

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              ФИО
            </label>
            <input
              id="fullName"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiMail className="text-gray-400" />
              </div>
              {/* Почта только для чтения: она приходит из Axion (профиль Битрикс24), и по
                  ней вход из Axion находит учётку. Своя смена почты позволяла занять
                  учётку коллеги (проверка безопасности 19.10.2026). */}
              <input
                id="email"
                type="email"
                value={user.email}
                readOnly
                aria-describedby="email-hint"
                className="w-full pl-10 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 sm:text-sm cursor-not-allowed"
              />
            </div>
            <p id="email-hint" className="mt-1 text-xs text-gray-500">
              Почта приходит из Axion (профиль Битрикс24). Если она неверна — обратитесь к
              администратору склада.
            </p>
          </div>

          <div className="text-sm text-gray-500">
            Роль: {user.is_admin ? 'Администратор' : 'Менеджер'}
          </div>

          <button
            type="submit"
            disabled={profileLoading}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            {profileLoading ? 'Сохранение...' : 'Сохранить данные'}
          </button>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiLock className="h-5 w-5 text-primary-600" />
          Смена пароля
        </h2>

        {passwordError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <FiAlertCircle className="mr-2 flex-shrink-0" />
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <FiCheckCircle className="mr-2 flex-shrink-0" />
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Текущий пароль
            </label>
            <input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Новый пароль
            </label>
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">{PASSWORD_HINT}</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Подтвердите новый пароль
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={passwordLoading}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            {passwordLoading ? 'Сохранение...' : 'Сменить пароль'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Profile;
